<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { renderTemplate } from "@/shared/services/template-renderer";
import { normalizeUserData } from "@/shared/services/user-normalizer";
import { isMobilePlatform } from "@/shared/services/platform";
import type { SignatureTemplate, UserTemplateData } from "@/shared/types";
import BaseButton from "@/shared/components/BaseButton.vue";

const MIN_SCALE = 0.4;
const RESIZE_DEBOUNCE_MS = 100;

const props = defineProps<{
  template: SignatureTemplate;
  userData: UserTemplateData;
  isApplying?: boolean;
  hasPrev?: boolean;
  hasNext?: boolean;
  index?: number;
  total?: number;
  transitionName?: string;
  previewThemeLight?: boolean;
}>();

const emit = defineEmits<{
  apply: [templateId: number];
  prev: [];
  next: [];
  toggleTheme: [];
}>();

const renderedHtml = computed(() =>
  renderTemplate(props.template.html, normalizeUserData(props.userData))
);

const isMobile = computed(() => isMobilePlatform());

const copying = ref(false);
const previewScale = ref(1);
const reservedHeight = ref(0);
const previewWrapRef = ref<HTMLElement | null>(null);
const previewBodyRef = ref<HTMLElement | null>(null);
let resizeObserver: ResizeObserver | null = null;
let resizeTimer: ReturnType<typeof setTimeout> | null = null;

function computeNaturalSize(): { width: number; height: number } {
  const body = previewBodyRef.value;
  if (!body) return { width: 420, height: 0 };
  const width = body.scrollWidth || body.offsetWidth || 420;
  const height = body.scrollHeight || body.offsetHeight || 0;
  return { width: width > 0 ? width : 420, height: height > 0 ? height : 0 };
}

function updatePreviewScale(): void {
  const wrap = previewWrapRef.value;
  if (!wrap) return;

  const cs = getComputedStyle(wrap);
  const padX = parseFloat(cs.paddingLeft || "0") + parseFloat(cs.paddingRight || "0");
  const availableWidth = wrap.clientWidth - padX;
  if (availableWidth <= 0) return;

  const natural = computeNaturalSize();
  if (natural.width <= 0) return;

  const scale = Math.min(1, Math.max(MIN_SCALE, availableWidth / natural.width));
  previewScale.value = scale;
  reservedHeight.value = Math.ceil(natural.height * scale);
}

function handleResize(): void {
  if (resizeTimer) clearTimeout(resizeTimer);
  resizeTimer = setTimeout(updatePreviewScale, RESIZE_DEBOUNCE_MS);
}

onMounted(() => {
  updatePreviewScale();
  if (typeof ResizeObserver !== "undefined" && previewWrapRef.value) {
    resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(previewWrapRef.value);
  }
  if (previewBodyRef.value) {
    previewBodyRef.value.addEventListener("load", updatePreviewScale, true);
  }
});

watch(
  () => props.template.id,
  () => {
    nextTick(updatePreviewScale);
  }
);

onUnmounted(() => {
  if (resizeTimer) clearTimeout(resizeTimer);
  if (resizeObserver) {
    resizeObserver.disconnect();
    resizeObserver = null;
  }
  if (previewBodyRef.value) {
    previewBodyRef.value.removeEventListener("load", updatePreviewScale, true);
  }
});

function togglePreviewTheme(): void {
  emit("toggleTheme");
}

async function copyToClipboard(): Promise<void> {
  copying.value = true;
  try {
    const htmlBlob = new Blob([renderedHtml.value], { type: "text/html" });
    const textBlob = new Blob([renderedHtml.value], { type: "text/plain" });
    const item = new ClipboardItem({
      "text/html": htmlBlob,
      "text/plain": textBlob,
    });
    await navigator.clipboard.write([item]);
  } catch {
    await navigator.clipboard.writeText(renderedHtml.value);
  } finally {
    copying.value = false;
  }
}
</script>

<template>
  <section class="sig-card">
    <div class="sig-card__toolbar">
      <h3 class="sig-card__template-name">{{ template.name }}</h3>

      <button
        v-if="!isMobile"
        class="sig-card__use-btn"
        type="button"
        :disabled="isApplying"
        :title="isApplying ? 'Aplicando...' : 'Aplicar assinatura'"
        @click="emit('apply', template.id)"
      >
        <v-icon name="md-check-outlined" />
        <span>{{ isApplying ? "..." : "Visualizar" }}</span>
      </button>

      <BaseButton
        v-else
        variant="primary"
        size="sm"
        :disabled="copying"
        @click="copyToClipboard"
        title="Copiar assinatura"
      >
        <v-icon name="md-save-outlined" />
      </BaseButton>
    </div>

    <div
      ref="previewWrapRef"
      class="sig-card__preview"
      v-bind:class="previewThemeLight ? 'sig-card__preview--light' : 'sig-card__preview--dark'"
    >
      <transition
        v-bind:name="transitionName || 'slide-left'"
        mode="out-in"
      >
        <div
          v-bind:key="template.id"
          class="sig-card__preview-reserve"
          v-bind:style="{ height: reservedHeight + 'px' }"
        >
          <div
            class="sig-card__preview-scaler"
            v-bind:style="{ transform: 'scale(' + previewScale + ')' }"
          >
            <div
              ref="previewBodyRef"
              class="sig-card__preview-body"
              v-html="renderedHtml"
            ></div>
          </div>
        </div>
      </transition>
    </div>

    <div class="sig-card__footer">
      <div class="sig-card__footer-left">
        <button
          v-if="hasPrev"
          class="sig-card__icon-btn"
          type="button"
          aria-label="Assinatura anterior"
          @click="emit('prev')"
        >
          <v-icon name="md-arrowbackiosnew-outlined" />
        </button>
        <button
          v-if="hasNext"
          class="sig-card__icon-btn"
          type="button"
          aria-label="Próxima assinatura"
          @click="emit('next')"
        >
          <v-icon name="md-arrowforwardios-outlined" />
        </button>
        <span class="sig-card__counter">
          {{ index ?? 1 }} de {{ total ?? 1 }}
        </span>
      </div>
      <div class="sig-card__footer-actions">
        <button
          class="sig-card__icon-btn sig-card__icon-btn--ghost"
          type="button"
          v-bind:title="previewThemeLight ? 'Ver no tema escuro' : 'Ver no tema claro'"
          v-on:click="togglePreviewTheme"
        >
          <v-icon
            v-bind:name="previewThemeLight ? 'md-nightlight-round' : 'md-lightmode-outlined'"
            class="sig-card__theme-icon"
          />
        </button>
        <button
          class="sig-card__icon-btn sig-card__icon-btn--ghost"
          type="button"
          v-bind:disabled="copying"
          v-bind:title="copying ? 'Copiando...' : 'Copiar HTML'"
          v-on:click="copyToClipboard"
        >
          <v-icon name="md-contentcopy-outlined" />
        </button>
      </div>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.sig-card {
  position: relative;
  background: var(--colorBackground);
  border: 1px solid var(--colorBorderSubtle);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow2);
  padding: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
}

.sig-card__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-sm);
  border-bottom: 1px solid var(--colorBorderSubtle);
  background: var(--colorBackgroundSubtle);
}

.sig-card__template-name {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: 600;
  color: var(--colorText);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.sig-card__icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border: 1px solid var(--colorBorder);
  border-radius: var(--radius-sm);
  background: var(--colorBackground);
  color: var(--colorTextSecondary);
  cursor: pointer;
  transition: background var(--transition), color var(--transition), border-color var(--transition);

  .ov-icon {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    background: var(--colorBackgroundHover);
    color: var(--colorText);
    border-color: var(--colorPrimary);
  }

  &:focus-visible {
    outline: 2px solid var(--colorBorderFocus);
    outline-offset: -2px;
  }
}

.sig-card__use-btn {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-xs) var(--spacing-sm);
  font-size: var(--font-size-sm);
  font-family: var(--font-family);
  font-weight: 500;
  color: var(--colorTextInverted);
  background: var(--colorPrimary);
  border: 1px solid var(--colorPrimary);
  border-radius: var(--radius-sm);
  cursor: pointer;
  transition: background var(--transition), border-color var(--transition), transform var(--transition);

  .ov-icon {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    background: var(--colorPrimaryHover);
    border-color: var(--colorPrimaryHover);
  }

  &:active:not(:disabled) {
    transform: scale(0.97);
  }

  &:focus-visible {
    outline: 2px solid var(--colorBorderFocus);
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.sig-card__preview {
  padding: var(--spacing-md);
  background: var(--colorBackgroundSubtle);
  overflow: hidden;
  flex: 1;
  position: relative;
  transition: background-color var(--transition), color var(--transition);
  display: flex;
  justify-content: flex-start;
  align-items: flex-start;
}

.sig-card__preview--light {
  --colorBackground: #ffffff;
  --colorBackgroundSubtle: #fafafa;
  --colorText: #242424;
  --colorTextSecondary: #424242;
  --colorTextTertiary: #616161;
  --colorBorder: #d6d6d6;
  --colorBorderSubtle: #e0e0e0;
  background: var(--colorBackgroundSubtle);
  color: var(--colorText);
}

.sig-card__preview--dark {
  --colorBackground: #1b1b1b;
  --colorBackgroundSubtle: #242424;
  --colorText: #ffffff;
  --colorTextSecondary: #d6d6d6;
  --colorTextTertiary: #adadad;
  --colorBorder: #4f4f4f;
  --colorBorderSubtle: #383838;
  background: var(--colorBackgroundSubtle);
  color: var(--colorText);
}

.sig-card__preview-reserve {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.sig-card__preview-scaler {
  position: absolute;
  top: 0;
  left: 0;
  transform-origin: top left;
  transition: transform 220ms ease, height 220ms ease;
  width: max-content;
  max-width: none;
}

.sig-card__preview-body {
  width: max-content;
  max-width: none;
}

.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 180ms ease;
}

.slide-left-enter-from {
  transform: translateX(80px);
  opacity: 0;
}
.slide-left-leave-to {
  transform: translateX(-80px);
  opacity: 0;
}

.slide-right-enter-from {
  transform: translateX(-80px);
  opacity: 0;
}
.slide-right-leave-to {
  transform: translateX(80px);
  opacity: 0;
}

.sig-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-xs) var(--spacing-sm);
  border-top: 1px solid var(--colorBorderSubtle);
  background: var(--colorBackgroundSubtle);
}

.sig-card__footer-left {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-sm);
}

.sig-card__counter {
  font-size: var(--font-size-sm);
  color: var(--colorTextTertiary);
  font-variant-numeric: tabular-nums;
}

.sig-card__footer-actions {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-xs);
}

.sig-card__icon-btn--ghost {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--colorTextSecondary);
  cursor: pointer;
  transition: background var(--transition), color var(--transition), transform var(--transition);

  .ov-icon {
    width: 16px;
    height: 16px;
  }

  &:hover:not(:disabled) {
    background: var(--colorBackgroundHover);
    color: var(--colorText);
  }

  &:active:not(:disabled) {
    transform: scale(0.95);
  }

  &:focus-visible {
    outline: 2px solid var(--colorBorderFocus);
    outline-offset: -2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.sig-card__theme-icon {
  transition: transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.sig-card__icon-btn--ghost:active .sig-card__theme-icon {
  transform: rotate(180deg);
}

@media (max-width: 400px) {
  .sig-card {
    border-radius: var(--radius-sm);
  }

  .sig-card__preview {
    padding: var(--spacing-sm);
  }
}
</style>
